import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import balanceAppTracking from "./BalanceAppTracking";
import { useWebhookOperation } from "./utils/webhookUtils";
import WebhookStatusDisplay from "./utils/WebhookStatusDisplay";
import ServiceHealthMonitor from "./components/ServiceHealthMonitor";
import { apiFetch } from "./config/apiConfig";

// Simple Tooltip Component
const Tooltip = ({ children, content, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 text-center">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// Info icon component for tooltips
const InfoIcon = ({ tooltip, className = "" }) => (
  <Tooltip content={tooltip} className={className}>
    <svg
      className="w-4 h-4 text-gray-400 hover:text-gray-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  </Tooltip>
);

InfoIcon.propTypes = {
  tooltip: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const AdminPage = ({ user, logout }) => {
  const navigate = useNavigate();

  // Webhook operation hooks
  const rssOperation = useWebhookOperation();
  const deduplicationOperation = useWebhookOperation();
  const cleanupOperation = useWebhookOperation();

  // State for admin dashboard data
  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);

  // State for story statistics
  const [storyStats, setStoryStats] = useState(null);
  const [storyStatsLoading, setStoryStatsLoading] = useState(false);

  // State for detailed news statistics
  const [newsStats, setNewsStats] = useState(null);
  const [newsStatsLoading, setNewsStatsLoading] = useState(false);

  // State for credibility config
  const [credibilityConfig, setCredibilityConfig] = useState(null);
  const [credibilityConfigLoading, setCredibilityConfigLoading] =
    useState(false);
  const [credibilityConfigEditing, setCredibilityConfigEditing] =
    useState(false);
  const [editingCredibilityConfig, setEditingCredibilityConfig] =
    useState(null);

  // State for OpenAI prompt config
  const [openaiPrompt, setOpenaiPrompt] = useState(null);
  const [openaiPromptLoading, setOpenaiPromptLoading] = useState(false);
  const [openaiPromptEditing, setOpenaiPromptEditing] = useState(false);
  const [editingOpenaiPrompt, setEditingOpenaiPrompt] = useState("");

  // State for comparative analysis prompt config
  const [comparativePrompt, setComparativePrompt] = useState(null);
  const [comparativePromptLoading, setComparativePromptLoading] =
    useState(false);
  const [comparativePromptEditing, setComparativePromptEditing] =
    useState(false);
  const [editingComparativePrompt, setEditingComparativePrompt] = useState("");

  // Add state for user management
  const [deletingUser, setDeletingUser] = useState(null);

  // State for RSS management
  const [rssConfig, setRssConfig] = useState(null);
  const [triggeringRssUpdate, setTriggeringRssUpdate] = useState(false);

  // State for deduplication
  const [triggeringDeduplication, setTriggeringDeduplication] = useState(false);

  // State for Source Limits configuration
  const [sourceLimits, setSourceLimits] = useState(null);
  const [sourceLimitsLoading, setSourceLimitsLoading] = useState(false);
  const [sourceLimitsEditing, setSourceLimitsEditing] = useState(false);
  const [editingSourceLimits, setEditingSourceLimits] = useState(null);

  // State for Relevance Scoring configuration
  const [relevanceConfig, setRelevanceConfig] = useState(null);
  const [relevanceConfigLoading, setRelevanceConfigLoading] = useState(false);
  const [relevanceConfigEditing, setRelevanceConfigEditing] = useState(false);
  const [editingRelevanceConfig, setEditingRelevanceConfig] = useState(null);

  // State for Advanced Settings configuration
  const [advancedConfig, setAdvancedConfig] = useState(null);
  const [advancedConfigLoading, setAdvancedConfigLoading] = useState(false);
  const [advancedConfigEditing, setAdvancedConfigEditing] = useState(false);
  const [editingAdvancedConfig, setEditingAdvancedConfig] = useState(null);

  const loadAdminDashboard = useCallback(async () => {
    setAdminLoading(true);
    try {
      const response = await apiFetch("/admin/dashboard");

      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const loadFeatureFlags = useCallback(async () => {
    setFeatureFlagsLoading(true);
    try {
      const response = await apiFetch("/admin/feature-flags", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFeatureFlags(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setFeatureFlagsLoading(false);
    }
  }, []);

  const loadStoryStats = useCallback(async () => {
    setStoryStatsLoading(true);
    try {
      const response = await apiFetch("/news/stories");
      if (response.ok) {
        const data = await response.json();
        setStoryStats({
          totalStories: data.count || 0,
          lastUpdated: data.last_updated || null,
        });
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setStoryStatsLoading(false);
    }
  }, []);

  const loadNewsStats = useCallback(async () => {
    setNewsStatsLoading(true);
    try {
      const response = await apiFetch(`/news/stories?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        setNewsStats({
          totalStories: data.count || 0,
          lastUpdated: data.last_updated || null,
          stories: data.stories || [],
        });
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setNewsStatsLoading(false);
    }
  }, []);

  const updateFeatureFlag = async (flagName, enabled) => {
    try {
      const response = await apiFetch("/admin/feature-flags", {
        method: "POST",
        body: JSON.stringify({ [flagName]: enabled }),
      });

      if (response.ok) {
        await loadFeatureFlags();
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  };

  // Credibility Configuration Functions
  const loadCredibilityConfig = useCallback(async () => {
    setCredibilityConfigLoading(true);
    try {
      const response = await apiFetch("/admin/credibility-config", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCredibilityConfig(data.credibilityConfig);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setCredibilityConfigLoading(false);
    }
  }, []);

  // OpenAI Prompt Configuration Functions
  const loadOpenaiPrompt = useCallback(async () => {
    setOpenaiPromptLoading(true);
    try {
      const response = await apiFetch("/admin/openai-prompt", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOpenaiPrompt(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setOpenaiPromptLoading(false);
    }
  }, []);

  const saveOpenaiPrompt = async () => {
    if (!editingOpenaiPrompt.trim()) return;

    setOpenaiPromptLoading(true);
    try {
      const response = await apiFetch("/admin/openai-prompt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify({ prompt: editingOpenaiPrompt }),
      });

      if (response.ok) {
        await loadOpenaiPrompt();
        setOpenaiPromptEditing(false);
        setEditingOpenaiPrompt("");
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setOpenaiPromptLoading(false);
    }
  };

  // Comparative Analysis Prompt Configuration Functions
  const loadComparativePrompt = useCallback(async () => {
    setComparativePromptLoading(true);
    try {
      const response = await apiFetch("/admin/comparative-analysis-prompt");

      if (response.ok) {
        const data = await response.json();
        setComparativePrompt(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setComparativePromptLoading(false);
    }
  }, []);

  const saveComparativePrompt = async () => {
    if (!editingComparativePrompt.trim()) return;

    setComparativePromptLoading(true);
    try {
      const response = await apiFetch("/admin/comparative-analysis-prompt", {
        method: "POST",
        body: JSON.stringify({ prompt: editingComparativePrompt }),
      });

      if (response.ok) {
        await loadComparativePrompt();
        setComparativePromptEditing(false);
        setEditingComparativePrompt("");
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setComparativePromptLoading(false);
    }
  };

  const saveCredibilityConfig = async () => {
    if (!editingCredibilityConfig) return;

    setCredibilityConfigLoading(true);
    try {
      const response = await apiFetch("/admin/credibility-config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify({ credibilityConfig: editingCredibilityConfig }),
      });

      if (response.ok) {
        await loadCredibilityConfig();
        setCredibilityConfigEditing(false);
        setEditingCredibilityConfig(null);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setCredibilityConfigLoading(false);
    }
  };

  const startEditingCredibilityConfig = () => {
    setEditingCredibilityConfig([...credibilityConfig]);
    setCredibilityConfigEditing(true);
  };

  const cancelEditingCredibilityConfig = () => {
    setEditingCredibilityConfig(null);
    setCredibilityConfigEditing(false);
  };

  const addCredibilityConfigRow = () => {
    const newRow = {
      threshold: 0.5,
      label: "New Label",
      color: "blue",
      meaning: "New meaning",
    };
    setEditingCredibilityConfig([...editingCredibilityConfig, newRow]);
  };

  const removeCredibilityConfigRow = (index) => {
    const newConfig = editingCredibilityConfig.filter((_, i) => i !== index);
    setEditingCredibilityConfig(newConfig);
  };

  const updateCredibilityConfigRow = (index, field, value) => {
    const newConfig = [...editingCredibilityConfig];
    newConfig[index] = { ...newConfig[index], [field]: value };
    setEditingCredibilityConfig(newConfig);
  };

  // Add delete user function
  const deleteUser = async (email) => {
    if (!email || deletingUser) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete user "${email}"?\n\nThis will permanently remove:\n- User account\n- All usage data\n- All sessions\n- All magic links\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingUser(email);
    try {
      const response = await apiFetch("/admin/users", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        await response.json();

        // Track user deletion
        balanceAppTracking.trackUserDelete(email);

        alert(`Successfully deleted user: ${email}`);
        // Reload dashboard data to reflect changes
        await loadAdminDashboard();
      } else {
        const errorData = await response.json();
        alert(`Error deleting user: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      alert(`Error deleting user: ${error.message}`);
    } finally {
      setDeletingUser(null);
    }
  };

  // Function to ensure admin session token exists
  const ensureAdminSession = useCallback(async () => {
    let sessionToken = localStorage.getItem("sessionToken");

    if (!sessionToken) {
      try {
        const response = await apiFetch("/dev/admin-session", {
          method: "POST",
        });

        if (response.ok) {
          const data = await response.json();
          sessionToken = data.sessionToken;
          localStorage.setItem("sessionToken", sessionToken);
        }
      } catch (error) {
        // Failed to create admin session - fallback to existing token
      }
    }

    return sessionToken;
  }, []);

  // RSS Management Functions
  const loadRssConfig = useCallback(async () => {
    // Loading RSS config
    try {
      const sessionToken = await ensureAdminSession();
      const response = await apiFetch("/admin/rss-config", {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRssConfig(data);
      } else if (response.status === 403) {
        // Try creating a new session
        localStorage.removeItem("sessionToken");
        const newSessionToken = await ensureAdminSession();
        if (newSessionToken) {
          // Retry with new token
          const retryResponse = await apiFetch("/admin/rss-config", {
            headers: {
              Authorization: `Bearer ${newSessionToken}`,
            },
          });
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setRssConfig(data);
          }
        }
      }
    } catch (error) {
      // Error loading RSS config - silent fail
    } finally {
      // RSS config loaded
    }
  }, [ensureAdminSession]);

  const triggerRssUpdate = async () => {
    setTriggeringRssUpdate(true);
    try {
      // Execute RSS aggregation with webhook support (will auto-get API key)
      await rssOperation.executeRSSAggregation(null, true);

      // Reload data after completion (either sync or async)
      // Note: For webhook operations, this will happen immediately,
      // actual reload should happen in the webhook completion callback
      if (!rssOperation.isRunning) {
        await loadAdminDashboard();
        await loadRssConfig();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering RSS update:", error);
    } finally {
      setTriggeringRssUpdate(false);
    }
  };

  const triggerDeduplication = async () => {
    setTriggeringDeduplication(true);
    try {
      // Execute story deduplication with webhook support (will auto-get API key)
      await deduplicationOperation.executeStoryDeduplication(null, true);

      // Reload data after completion (either sync or async)
      if (!deduplicationOperation.isRunning) {
        await loadAdminDashboard();
        await loadStoryStats();
        await loadNewsStats();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering deduplication:", error);
    } finally {
      setTriggeringDeduplication(false);
    }
  };

  const triggerCleanupOrphanedStories = async () => {
    try {
      // Execute cleanup orphaned stories with webhook support (will auto-get API key)
      await cleanupOperation.executeCleanupOrphanedStories(null, true);

      // Reload data after completion (either sync or async)
      if (!cleanupOperation.isRunning) {
        await loadAdminDashboard();
        await loadStoryStats();
        await loadNewsStats();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error triggering cleanup:", error);
    }
  };

  // Source Limits Configuration Functions
  const loadSourceLimits = useCallback(async () => {
    setSourceLimitsLoading(true);
    try {
      const response = await apiFetch("/admin/source-limits", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSourceLimits(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setSourceLimitsLoading(false);
    }
  }, []);

  const saveSourceLimits = async () => {
    if (!editingSourceLimits) return;

    setSourceLimitsLoading(true);
    try {
      const response = await apiFetch("/admin/source-limits", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify(editingSourceLimits),
      });

      if (response.ok) {
        await loadSourceLimits();
        setSourceLimitsEditing(false);
        setEditingSourceLimits(null);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setSourceLimitsLoading(false);
    }
  };

  const startEditingSourceLimits = () => {
    setEditingSourceLimits({ ...sourceLimits });
    setSourceLimitsEditing(true);
  };

  const cancelEditingSourceLimits = () => {
    setEditingSourceLimits(null);
    setSourceLimitsEditing(false);
  };

  const updateSourceLimitField = (field, value) => {
    setEditingSourceLimits((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Relevance Configuration Functions
  const loadRelevanceConfig = useCallback(async () => {
    setRelevanceConfigLoading(true);
    try {
      const response = await apiFetch("/admin/relevance-config", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRelevanceConfig(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setRelevanceConfigLoading(false);
    }
  }, []);

  const saveRelevanceConfig = async () => {
    if (!editingRelevanceConfig) return;

    setRelevanceConfigLoading(true);
    try {
      // eslint-disable-next-line no-console
      console.log("Saving relevance config:", editingRelevanceConfig);

      const response = await apiFetch("/admin/relevance-config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify(editingRelevanceConfig),
      });

      if (response.ok) {
        await loadRelevanceConfig();
        setRelevanceConfigEditing(false);
        setEditingRelevanceConfig(null);
      } else {
        const errorData = await response.text();
        // eslint-disable-next-line no-console
        console.error(
          "Error saving relevance config:",
          response.status,
          errorData
        );
        alert(
          `Error saving relevance config: ${response.status} - ${errorData}`
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving relevance config:", error);
      alert(`Error saving relevance config: ${error.message}`);
    } finally {
      setRelevanceConfigLoading(false);
    }
  };

  const startEditingRelevanceConfig = () => {
    setEditingRelevanceConfig({ ...relevanceConfig });
    setRelevanceConfigEditing(true);
  };

  const cancelEditingRelevanceConfig = () => {
    setEditingRelevanceConfig(null);
    setRelevanceConfigEditing(false);
  };

  const updateRelevanceConfigField = (field, value) => {
    // Validate ranges to prevent invalid values
    let validatedValue = value;

    if (field === "threshold") {
      validatedValue = Math.max(0.1, Math.min(1.0, parseFloat(value) || 0.1));
    } else if (field === "min_articles") {
      validatedValue = Math.max(0, Math.min(50, parseInt(value) || 0));
    } else if (field === "max_articles_to_score") {
      validatedValue = Math.max(1, Math.min(1000, parseInt(value) || 1));
    }

    setEditingRelevanceConfig((prev) => ({
      ...prev,
      [field]: validatedValue,
    }));
  };

  // Advanced Configuration Functions
  const loadAdvancedConfig = useCallback(async () => {
    setAdvancedConfigLoading(true);
    try {
      const response = await apiFetch("/admin/advanced-config", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAdvancedConfig(data);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setAdvancedConfigLoading(false);
    }
  }, []);

  const saveAdvancedConfig = async () => {
    if (!editingAdvancedConfig) return;

    setAdvancedConfigLoading(true);
    try {
      const response = await apiFetch("/admin/advanced-config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
        },
        body: JSON.stringify(editingAdvancedConfig),
      });

      if (response.ok) {
        await loadAdvancedConfig();
        setAdvancedConfigEditing(false);
        setEditingAdvancedConfig(null);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      setAdvancedConfigLoading(false);
    }
  };

  const startEditingAdvancedConfig = () => {
    setEditingAdvancedConfig({ ...advancedConfig });
    setAdvancedConfigEditing(true);
  };

  const cancelEditingAdvancedConfig = () => {
    setEditingAdvancedConfig(null);
    setAdvancedConfigEditing(false);
  };

  const updateAdvancedConfigField = (field, value) => {
    setEditingAdvancedConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Load admin data on component mount
  useEffect(() => {
    // Track admin dashboard access
    balanceAppTracking.trackAdminDashboardAccess();

    loadAdminDashboard();
    loadFeatureFlags();
    loadCredibilityConfig();
    loadOpenaiPrompt();
    loadComparativePrompt();
    loadRssConfig();
    loadStoryStats();
    loadNewsStats();
    loadSourceLimits();
    loadRelevanceConfig();
    loadAdvancedConfig();
  }, [
    loadAdminDashboard,
    loadFeatureFlags,
    loadCredibilityConfig,
    loadOpenaiPrompt,
    loadComparativePrompt,
    loadRssConfig,
    loadStoryStats,
    loadNewsStats,
    loadSourceLimits,
    loadRelevanceConfig,
    loadAdvancedConfig,
  ]);

  // Check if user is admin with fallback to localStorage
  const isUserAdmin = () => {
    // Primary check: current user state
    if (user?.isAdmin) return true;

    // Fallback check: localStorage userData
    try {
      const storedUserData = localStorage.getItem("userData");
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        // Check stored user data for admin status
        return userData.isAdmin === true;
      }
    } catch (err) {
      // Error parsing stored user data
    }

    return false;
  };

  if (!isUserAdmin()) {
    // Access denied - user is not admin

    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-neutral-900 mb-2">
            Access Denied
          </h1>
          <p className="text-neutral-600 mb-4">
            You don't have permission to access this page.
          </p>
          <button onClick={() => navigate("/")} className="btn-primary">
            Go Back to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-neutral-900">
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-neutral-600">
                Welcome, {user.email}
              </span>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ← Back to App
              </button>
              <button onClick={logout} className="btn-primary">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Overview Stats */}
        {adminLoading || storyStatsLoading || newsStatsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner mr-3"></div>
            <span className="text-neutral-600">Loading dashboard...</span>
          </div>
        ) : adminStats || storyStats || newsStats ? (
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-neutral-900">Overview</h2>
              <button
                onClick={() => {
                  loadStoryStats();
                  loadNewsStats();
                  loadAdminDashboard();
                }}
                disabled={storyStatsLoading || newsStatsLoading || adminLoading}
                className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {storyStatsLoading || newsStatsLoading || adminLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-600 mr-2"></div>
                    Refreshing...
                  </>
                ) : (
                  <>↻ Refresh Stats</>
                )}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {adminStats && (
                <div className="bg-primary-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary-600">
                    {adminStats.totalUsers}
                  </div>
                  <div className="text-sm text-neutral-600">Total Users</div>
                </div>
              )}

              {storyStats && (
                <>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {storyStats.totalStories}
                    </div>
                    <div className="text-sm text-neutral-600">
                      Stories Stored
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {rssConfig?.feeds?.length || 0}
                    </div>
                    <div className="text-sm text-neutral-600">RSS Sources</div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-sm font-medium text-purple-600">
                      {storyStats.lastUpdated
                        ? new Date(storyStats.lastUpdated).toLocaleString()
                        : "Never"}
                    </div>
                    <div className="text-sm text-neutral-600">Last Updated</div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800 text-sm">
              Failed to load dashboard data
            </div>
          </div>
        )}

        {/* Detailed News Statistics */}
        {newsStats && (
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-neutral-900">
                News Statistics
              </h2>
              <span className="text-xs text-gray-500">
                {newsStats.lastUpdated
                  ? `Last updated: ${new Date(newsStats.lastUpdated).toLocaleString()}`
                  : "Never updated"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {newsStats.activeStories}
                </div>
                <div className="text-sm text-neutral-600">Active Stories</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {newsStats.leftArticles}
                </div>
                <div className="text-sm text-neutral-600">Left Articles</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {newsStats.centerArticles}
                </div>
                <div className="text-sm text-neutral-600">Center Articles</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {newsStats.rightArticles}
                </div>
                <div className="text-sm text-neutral-600">Right Articles</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-lg font-medium text-gray-900">
                  Total Articles: {newsStats.totalArticles}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Bias Distribution:{" "}
                  {newsStats.totalArticles > 0
                    ? `${Math.round((newsStats.leftArticles / newsStats.totalArticles) * 100)}% Left, ${Math.round((newsStats.centerArticles / newsStats.totalArticles) * 100)}% Center, ${Math.round((newsStats.rightArticles / newsStats.totalArticles) * 100)}% Right`
                    : "No data"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Management */}
        {adminStats && (
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
            <h2 className="text-lg font-medium text-neutral-900 mb-4">
              User Management ({adminStats.users?.length || 0} users)
            </h2>
            {!adminStats.users || adminStats.users.length === 0 ? (
              <div className="text-neutral-600 text-center py-8">
                No users found in the system.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Daily Limit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Usage Today
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {adminStats.users.map((userData, index) => (
                      <tr
                        key={userData.email || index}
                        className="hover:bg-neutral-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center mr-3">
                              <span className="text-white font-medium text-xs">
                                {userData.email?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-neutral-900">
                                {userData.email}
                              </div>
                              {userData.isAdmin && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              userData.tier === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : userData.tier === "pro"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                            }`}
                          >
                            {userData.tier || "free"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                          {userData.dailyLimit === 999999
                            ? "Unlimited"
                            : userData.dailyLimit || 10}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                          {userData.usageToday || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {userData.createdAt
                            ? new Date(userData.createdAt).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {userData.lastLoginAt
                            ? new Date(
                                userData.lastLoginAt
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {userData.email !== user?.email && (
                            <button
                              onClick={() => deleteUser(userData.email)}
                              disabled={deletingUser === userData.email}
                              className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingUser === userData.email ? (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
                                  Deleting...
                                </div>
                              ) : (
                                "Delete"
                              )}
                            </button>
                          )}
                          {userData.email === user?.email && (
                            <span className="text-neutral-400 italic">
                              Current User
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Feature Flags */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-medium text-neutral-900">
              News API Feature Flags
            </h2>
            <InfoIcon tooltip="Enable or disable different news sources for article aggregation. Disabled sources won't be used to gather news articles during the automated RSS aggregation process." />
          </div>
          {featureFlagsLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading feature flags...
              </span>
            </div>
          ) : featureFlags ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Google News */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div>
                      <h4 className="font-medium text-neutral-900">
                        Google News RSS
                      </h4>
                      <p className="text-sm text-neutral-600">Free RSS feed</p>
                    </div>
                    <InfoIcon
                      tooltip="Free Google News RSS feed. Provides general news articles from various sources. No API key required."
                      className="ml-1"
                    />
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "GOOGLE_NEWS_ENABLED",
                        !featureFlags.news_sources.google_news_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.google_news_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.google_news_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* NewsAPI */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div>
                      <h4 className="font-medium text-neutral-900">
                        NewsAPI.org
                      </h4>
                      <p className="text-sm text-neutral-600">
                        Requires API key
                      </p>
                    </div>
                    <InfoIcon
                      tooltip="NewsAPI.org provides articles from major news sources. Requires API key. Good for mainstream media coverage."
                      className="ml-1"
                    />
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "NEWSAPI_ENABLED",
                        !featureFlags.news_sources.newsapi_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.newsapi_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.newsapi_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* GNews */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div>
                      <h4 className="font-medium text-neutral-900">
                        GNews API
                      </h4>
                      <p className="text-sm text-neutral-600">
                        Requires API key
                      </p>
                    </div>
                    <InfoIcon
                      tooltip="GNews API provides fast, real-time news from various sources. Requires API key. Good supplement to other sources."
                      className="ml-1"
                    />
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "GNEWS_ENABLED",
                        !featureFlags.news_sources.gnews_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.gnews_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.gnews_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* Currents API */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div>
                      <h4 className="font-medium text-neutral-900">
                        Currents API
                      </h4>
                      <p className="text-sm text-neutral-600">
                        Requires API key
                      </p>
                    </div>
                    <InfoIcon
                      tooltip="Currents API provides news articles with good regional coverage. Requires API key. Useful for diverse geographic perspectives."
                      className="ml-1"
                    />
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "CURRENTS_API_ENABLED",
                        !featureFlags.news_sources.currents_api_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.currents_api_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.currents_api_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* Twingly */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-neutral-900">Twingly</h4>
                    <p className="text-sm text-neutral-600">Requires API key</p>
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "TWINGLY_ENABLED",
                        !featureFlags.news_sources.twingly_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.twingly_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.twingly_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* Bright Data */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-neutral-900">
                      Bright Data SERP
                    </h4>
                    <p className="text-sm text-neutral-600">Requires API key</p>
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "BRIGHT_DATA_ENABLED",
                        !featureFlags.news_sources.bright_data_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.bright_data_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.bright_data_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* RSS Feeds */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-neutral-900">RSS Feeds</h4>
                    <p className="text-sm text-neutral-600">Custom RSS feeds</p>
                  </div>
                  <button
                    onClick={() =>
                      updateFeatureFlag(
                        "RSS_FEEDS_ENABLED",
                        !featureFlags.news_sources.rss_feeds_enabled
                      )
                    }
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      featureFlags.news_sources.rss_feeds_enabled
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {featureFlags.news_sources.rss_feeds_enabled
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load feature flags
            </div>
          )}
        </div>

        {/* Credibility Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-neutral-900">
              Credibility Scoring Configuration
            </h2>
            {!credibilityConfigEditing ? (
              <button
                onClick={startEditingCredibilityConfig}
                className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ✏️ Edit Configuration
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={saveCredibilityConfig}
                  disabled={credibilityConfigLoading}
                  className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                >
                  {credibilityConfigLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    "✅ Save Changes"
                  )}
                </button>
                <button
                  onClick={cancelEditingCredibilityConfig}
                  disabled={credibilityConfigLoading}
                  className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 transition-colors disabled:opacity-50"
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>

          {credibilityConfigLoading && !credibilityConfigEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading credibility configuration...
              </span>
            </div>
          ) : credibilityConfig || editingCredibilityConfig ? (
            <div className="space-y-4">
              <div className="text-sm text-neutral-600">
                Configure how credibility scores are displayed to users. Higher
                thresholds should have better labels.
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Threshold (≥)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Text Label
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Color
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Meaning
                      </th>
                      {credibilityConfigEditing && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {(credibilityConfigEditing
                      ? editingCredibilityConfig
                      : credibilityConfig
                    )?.map((config, index) => (
                      <tr key={index} className="hover:bg-neutral-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {credibilityConfigEditing ? (
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.1"
                              value={config.threshold}
                              onChange={(e) =>
                                updateCredibilityConfigRow(
                                  index,
                                  "threshold",
                                  parseFloat(e.target.value) || 0.0
                                )
                              }
                              className="w-20 px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          ) : (
                            <span className="text-sm font-medium text-neutral-900">
                              {config.threshold}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {credibilityConfigEditing ? (
                            <input
                              type="text"
                              value={config.label}
                              onChange={(e) =>
                                updateCredibilityConfigRow(
                                  index,
                                  "label",
                                  e.target.value
                                )
                              }
                              className="w-24 px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          ) : (
                            <span className="text-sm font-medium text-neutral-900">
                              {config.label}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {credibilityConfigEditing ? (
                            <select
                              value={config.color}
                              onChange={(e) =>
                                updateCredibilityConfigRow(
                                  index,
                                  "color",
                                  e.target.value
                                )
                              }
                              className="px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              <option value="red">Red</option>
                              <option value="orange">Orange</option>
                              <option value="yellow">Yellow</option>
                              <option value="emerald">Emerald</option>
                              <option value="blue">Blue</option>
                              <option value="purple">Purple</option>
                            </select>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
                            >
                              {config.color}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {credibilityConfigEditing ? (
                            <input
                              type="text"
                              value={config.meaning}
                              onChange={(e) =>
                                updateCredibilityConfigRow(
                                  index,
                                  "meaning",
                                  e.target.value
                                )
                              }
                              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          ) : (
                            <span className="text-sm text-neutral-700">
                              {config.meaning}
                            </span>
                          )}
                        </td>
                        {credibilityConfigEditing && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => removeCredibilityConfigRow(index)}
                              className="text-red-600 hover:text-red-700 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {credibilityConfigEditing && (
                <div className="flex justify-start">
                  <button
                    onClick={addCredibilityConfigRow}
                    className="inline-flex items-center px-3 py-2 border border-primary-300 text-sm font-medium rounded text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    + Add Row
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load credibility configuration
            </div>
          )}
        </div>

        {/* OpenAI Analysis Prompt Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-neutral-900">
              OpenAI Analysis Prompt Configuration
            </h2>
            {!openaiPromptEditing ? (
              <button
                onClick={() => {
                  setEditingOpenaiPrompt(openaiPrompt?.prompt || "");
                  setOpenaiPromptEditing(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ✏️ Edit Prompt
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={saveOpenaiPrompt}
                  disabled={openaiPromptLoading}
                  className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                >
                  {openaiPromptLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    "✅ Save Changes"
                  )}
                </button>
                <button
                  onClick={() => {
                    setOpenaiPromptEditing(false);
                    setEditingOpenaiPrompt("");
                  }}
                  disabled={openaiPromptLoading}
                  className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 transition-colors disabled:opacity-50"
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>

          {openaiPromptLoading && !openaiPromptEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading OpenAI prompt configuration...
              </span>
            </div>
          ) : openaiPrompt ? (
            <div className="space-y-4">
              <div className="text-sm text-neutral-600">
                Configure the prompt used for OpenAI analysis. This affects how
                articles are analyzed for bias and credibility.
              </div>

              {openaiPromptEditing ? (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Analysis Prompt
                  </label>
                  <textarea
                    value={editingOpenaiPrompt}
                    onChange={(e) => setEditingOpenaiPrompt(e.target.value)}
                    rows={15}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-mono"
                    placeholder="Enter the OpenAI analysis prompt..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Current Analysis Prompt
                  </label>
                  <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                    {openaiPrompt.prompt}
                  </div>
                  {openaiPrompt.last_updated && (
                    <div className="text-xs text-neutral-500 mt-2">
                      Last updated:{" "}
                      {new Date(openaiPrompt.last_updated).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load OpenAI prompt configuration
            </div>
          )}
        </div>

        {/* Comparative Analysis Prompt Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-neutral-900">
              Comparative Analysis Prompt Configuration
            </h2>
            {!comparativePromptEditing ? (
              <button
                onClick={() => {
                  setEditingComparativePrompt(comparativePrompt?.prompt || "");
                  setComparativePromptEditing(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ✏️ Edit Prompt
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={saveComparativePrompt}
                  disabled={comparativePromptLoading}
                  className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                >
                  {comparativePromptLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    "✅ Save Changes"
                  )}
                </button>
                <button
                  onClick={() => {
                    setComparativePromptEditing(false);
                    setEditingComparativePrompt("");
                  }}
                  disabled={comparativePromptLoading}
                  className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 transition-colors disabled:opacity-50"
                >
                  ❌ Cancel
                </button>
              </div>
            )}
          </div>

          {comparativePromptLoading && !comparativePromptEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading comparative analysis prompt configuration...
              </span>
            </div>
          ) : comparativePrompt ? (
            <div className="space-y-4">
              <div className="text-sm text-neutral-600">
                Configure the prompt used for comparative analysis across
                different political perspectives.
              </div>

              {comparativePromptEditing ? (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Comparative Analysis Prompt
                  </label>
                  <textarea
                    value={editingComparativePrompt}
                    onChange={(e) =>
                      setEditingComparativePrompt(e.target.value)
                    }
                    rows={15}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-mono"
                    placeholder="Enter the comparative analysis prompt..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Current Comparative Analysis Prompt
                  </label>
                  <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                    {comparativePrompt.prompt}
                  </div>
                  {comparativePrompt.last_updated && (
                    <div className="text-xs text-neutral-500 mt-2">
                      Last updated:{" "}
                      {new Date(
                        comparativePrompt.last_updated
                      ).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load comparative analysis prompt configuration
            </div>
          )}
        </div>

        {/* RSS Feed Management */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-neutral-900">
                📰 RSS Feed & News Management
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Manage RSS feed sources, configuration, and updates
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate("/admin/rss")}
                className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                ⚙️ Manage RSS Feeds
              </button>
              <button
                onClick={() => navigate("/admin/monitoring")}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                📊 System Monitoring
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {rssConfig?.feeds?.length || 0}
              </div>
              <div className="text-sm text-blue-700">Total RSS Feeds</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {rssConfig?.feeds?.filter((f) => f && f.enabled).length || 0}
              </div>
              <div className="text-sm text-green-700">Active Feeds</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {rssConfig?.feeds
                  ?.filter((f) => f && f.enabled)
                  .reduce((sum, f) => sum + (f.max_articles || 10), 0) || 0}
              </div>
              <div className="text-sm text-purple-700">Max Articles/Cycle</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <div className="text-sm font-medium text-orange-600">
                {rssConfig?.lastUpdate
                  ? new Date(rssConfig.lastUpdate).toLocaleString()
                  : "Never"}
              </div>
              <div className="text-sm text-orange-700">Last Update</div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={triggerRssUpdate}
                disabled={triggeringRssUpdate || rssOperation.isRunning}
                className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {triggeringRssUpdate || rssOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    {rssOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🔄 RSS Aggregation"
                )}
              </button>
              <button
                onClick={triggerDeduplication}
                disabled={
                  triggeringDeduplication || deduplicationOperation.isRunning
                }
                className="inline-flex items-center px-3 py-2 border border-orange-300 text-sm font-medium rounded text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors disabled:opacity-50"
              >
                {triggeringDeduplication || deduplicationOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                    {deduplicationOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🧹 Deduplicate Stories"
                )}
              </button>
              <button
                onClick={triggerCleanupOrphanedStories}
                disabled={cleanupOperation.isRunning}
                className="inline-flex items-center px-3 py-2 border border-purple-300 text-sm font-medium rounded text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50"
              >
                {cleanupOperation.isRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                    {cleanupOperation.isRunning
                      ? "Running in Background..."
                      : "Starting..."}
                  </>
                ) : (
                  "🗑️ Cleanup Orphaned Stories"
                )}
              </button>
            </div>

            {/* Webhook Status Displays */}
            <WebhookStatusDisplay
              isRunning={rssOperation.isRunning}
              status={rssOperation.status}
              result={rssOperation.result}
              error={rssOperation.error}
              operationName="RSS Aggregation"
              onCancel={rssOperation.cancelOperation}
              onReset={rssOperation.resetState}
            />
            <WebhookStatusDisplay
              isRunning={deduplicationOperation.isRunning}
              status={deduplicationOperation.status}
              result={deduplicationOperation.result}
              error={deduplicationOperation.error}
              operationName="Story Deduplication"
              onCancel={deduplicationOperation.cancelOperation}
              onReset={deduplicationOperation.resetState}
            />
            <WebhookStatusDisplay
              isRunning={cleanupOperation.isRunning}
              status={cleanupOperation.status}
              result={cleanupOperation.result}
              error={cleanupOperation.error}
              operationName="Cleanup Orphaned Stories"
              onCancel={cleanupOperation.cancelOperation}
              onReset={cleanupOperation.resetState}
            />
          </div>
        </div>

        {/* News Source Limits Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-neutral-900">
                News Source Article Limits
              </h2>
              <InfoIcon tooltip="Set the maximum number of articles to fetch from each news source during aggregation. Higher limits provide more content but may increase processing time and API costs." />
            </div>
            <div className="flex items-center space-x-2">
              {sourceLimitsEditing ? (
                <>
                  <button
                    onClick={saveSourceLimits}
                    disabled={sourceLimitsLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sourceLimitsLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    onClick={cancelEditingSourceLimits}
                    disabled={sourceLimitsLoading}
                    className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={startEditingSourceLimits}
                  disabled={sourceLimitsLoading}
                  className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Edit Limits
                </button>
              )}
            </div>
          </div>

          {sourceLimitsLoading && !sourceLimitsEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading source limits...
              </span>
            </div>
          ) : sourceLimits ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Google News Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.google_news_max_articles || 20}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "google_news_max_articles",
                        parseInt(e.target.value) || 20
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.google_news_max_articles || 20}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  NewsAPI Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.newsapi_max_articles || 15}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "newsapi_max_articles",
                        parseInt(e.target.value) || 15
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.newsapi_max_articles || 15}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  GNews Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.gnews_max_articles || 10}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "gnews_max_articles",
                        parseInt(e.target.value) || 10
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.gnews_max_articles || 10}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Currents API Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.currents_max_articles || 20}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "currents_max_articles",
                        parseInt(e.target.value) || 20
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.currents_max_articles || 20}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Twingly Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.twingly_max_articles || 15}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "twingly_max_articles",
                        parseInt(e.target.value) || 15
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.twingly_max_articles || 15}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Bright Data Max Articles
                </label>
                {sourceLimitsEditing ? (
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={editingSourceLimits?.bright_data_max_articles || 10}
                    onChange={(e) =>
                      updateSourceLimitField(
                        "bright_data_max_articles",
                        parseInt(e.target.value) || 10
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {sourceLimits.bright_data_max_articles || 10}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load source limits configuration
            </div>
          )}
        </div>

        {/* Relevance Scoring Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-neutral-900">
                Relevance Scoring Settings
              </h2>
              <InfoIcon tooltip="Configure semantic relevance scoring to filter suggested articles based on similarity to the original article. Uses AI embeddings to score relevance." />
            </div>
            <div className="flex items-center space-x-2">
              {relevanceConfigEditing ? (
                <>
                  <button
                    onClick={saveRelevanceConfig}
                    disabled={relevanceConfigLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {relevanceConfigLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    onClick={cancelEditingRelevanceConfig}
                    disabled={relevanceConfigLoading}
                    className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={startEditingRelevanceConfig}
                  disabled={relevanceConfigLoading}
                  className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Edit Settings
                </button>
              )}
            </div>
          </div>

          {relevanceConfigLoading && !relevanceConfigEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading relevance configuration...
              </span>
            </div>
          ) : relevanceConfig ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    Relevance Threshold
                  </label>
                  <InfoIcon
                    tooltip="Minimum semantic similarity score (0.1-1.0) for articles to be considered relevant. Higher values = more selective filtering."
                    className="text-xs"
                  />
                </div>
                {relevanceConfigEditing ? (
                  <input
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    value={editingRelevanceConfig?.threshold || 0.25}
                    onChange={(e) =>
                      updateRelevanceConfigField(
                        "threshold",
                        parseFloat(e.target.value) || 0.25
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {((relevanceConfig.threshold || 0.25) * 100).toFixed(0)}%
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    Min Articles
                  </label>
                  <InfoIcon
                    tooltip="Minimum number of articles to return, even if they don't meet the relevance threshold. Ensures you always get some results."
                    className="text-xs"
                  />
                </div>
                {relevanceConfigEditing ? (
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={editingRelevanceConfig?.min_articles || 3}
                    onChange={(e) =>
                      updateRelevanceConfigField(
                        "min_articles",
                        parseInt(e.target.value) || 3
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {relevanceConfig.min_articles || 3}
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    Max Articles to Score
                  </label>
                  <InfoIcon
                    tooltip="Maximum number of articles to analyze for relevance (1-1000). Higher numbers increase accuracy but use more AI processing and cost more. Values over 100 may require longer processing times."
                    className="text-xs"
                  />
                </div>
                {relevanceConfigEditing ? (
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={editingRelevanceConfig?.max_articles_to_score || 20}
                    onChange={(e) =>
                      updateRelevanceConfigField(
                        "max_articles_to_score",
                        parseInt(e.target.value) || 20
                      )
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                ) : (
                  <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                    {relevanceConfig.max_articles_to_score || 20}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load relevance scoring configuration
            </div>
          )}
        </div>

        {/* Advanced Aggregation Settings */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-neutral-900">
                Advanced Aggregation Settings
              </h2>
              <InfoIcon tooltip="Core configuration settings that control how the news aggregation system processes and clusters articles into stories." />
            </div>
            <div className="flex items-center space-x-2">
              {advancedConfigEditing ? (
                <>
                  <button
                    onClick={saveAdvancedConfig}
                    disabled={advancedConfigLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {advancedConfigLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    onClick={cancelEditingAdvancedConfig}
                    disabled={advancedConfigLoading}
                    className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={startEditingAdvancedConfig}
                  disabled={advancedConfigLoading}
                  className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Edit Settings
                </button>
              )}
            </div>
          </div>

          {advancedConfigLoading && !advancedConfigEditing ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              <span className="text-sm text-neutral-600">
                Loading advanced configuration...
              </span>
            </div>
          ) : advancedConfig ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-neutral-700">
                      Max Total Articles
                    </label>
                    <InfoIcon
                      tooltip="Maximum total number of articles to store in the database. Currently set to 100,000 for large-scale aggregation."
                      className="text-xs"
                    />
                  </div>
                  {advancedConfigEditing ? (
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={editingAdvancedConfig?.max_total_articles || 50}
                      onChange={(e) =>
                        updateAdvancedConfigField(
                          "max_total_articles",
                          parseInt(e.target.value) || 50
                        )
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                      {advancedConfig.max_total_articles || 50}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-neutral-700">
                      Semantic Clustering Threshold
                    </label>
                    <InfoIcon
                      tooltip="Controls how similar articles need to be to cluster together into stories. Higher values (0.7-0.9) create fewer, more precise clusters. Lower values (0.5-0.65) create broader story groupings."
                      className="text-xs"
                    />
                  </div>
                  {advancedConfigEditing ? (
                    <input
                      type="number"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={
                        editingAdvancedConfig?.semantic_clustering_threshold ||
                        0.65
                      }
                      onChange={(e) =>
                        updateAdvancedConfigField(
                          "semantic_clustering_threshold",
                          parseFloat(e.target.value) || 0.65
                        )
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <div className="text-sm font-mono bg-neutral-50 px-3 py-2 border border-neutral-200 rounded">
                      {(
                        (advancedConfig.semantic_clustering_threshold || 0.65) *
                        100
                      ).toFixed(0)}
                      %
                    </div>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    Higher values = more selective clustering (0.1-1.0)
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enable_deduplication"
                    checked={
                      advancedConfigEditing
                        ? editingAdvancedConfig?.enable_deduplication
                        : advancedConfig.enable_deduplication
                    }
                    onChange={(e) =>
                      advancedConfigEditing &&
                      updateAdvancedConfigField(
                        "enable_deduplication",
                        e.target.checked
                      )
                    }
                    disabled={!advancedConfigEditing}
                    className="mr-2"
                  />
                  <label
                    htmlFor="enable_deduplication"
                    className="text-sm text-neutral-700"
                  >
                    Enable Advanced Deduplication
                  </label>
                  <InfoIcon
                    tooltip="Removes duplicate articles during aggregation using title similarity and content matching. Keeps the database clean and prevents redundant content."
                    className="ml-2"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="opposing_viewpoints"
                    checked={
                      advancedConfigEditing
                        ? editingAdvancedConfig?.opposing_viewpoints_enabled
                        : advancedConfig.opposing_viewpoints_enabled
                    }
                    onChange={(e) =>
                      advancedConfigEditing &&
                      updateAdvancedConfigField(
                        "opposing_viewpoints_enabled",
                        e.target.checked
                      )
                    }
                    disabled={!advancedConfigEditing}
                    className="mr-2"
                  />
                  <label
                    htmlFor="opposing_viewpoints"
                    className="text-sm text-neutral-700"
                  >
                    Enable Opposing Viewpoints
                  </label>
                  <InfoIcon
                    tooltip="When suggesting articles, prioritize showing opposing political viewpoints to promote balanced perspective and reduce echo chambers."
                    className="ml-2"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-red-800 text-sm">
              Failed to load advanced configuration
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

AdminPage.propTypes = {
  user: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
};

export default AdminPage;
